import React, { useEffect, useState } from 'react';
import { LuFocus, LuLandmark, LuCompass, LuUser, LuClipboardList, LuHouse } from 'react-icons/lu';
import AppShellSidebar from './AppShellSidebar';
import { HeaderAccountContext } from './HeaderAccount';
import BottomTabBar from './BottomTabBar';
import BannerAd from './BannerAd';
import ProBanner from './ProBanner';
import { HeaderScrollContext } from './HeaderSubtitle';
import TutorialOverlay from '../tutorial/TutorialOverlay';
import { ROUTES } from '../../contracts/routes';
import { adsEnabled } from '../../config/ads';

// In-app shell wrapper (design-recreation mockup). The whole DOCUMENT scrolls
// (the <body>), not an inner element — this is what lets mobile Safari/Chrome
// collapse their URL/search bar on scroll, and it removes the nested scrollbar
// the old 100vh inner-scroll shell produced. The sidebar (desktop) and bottom
// tab bar (mobile) are pinned to the viewport so they never scroll; the page
// header is hoisted here too, sticky to the top and condensing as you scroll.
//
//   ┌──────────┬──────────────────────────────┐
//   │ sidebar  │  StickyHeader (sticky top-0)  │   the document scrolls;
//   │ (sticky) │  page content (children)      │   sidebar + tab bar + header
//   └──────────┴──────────────────────────────┘   stay pinned to the viewport
//
// NAV SHAPE, and why it differs by surface.
//
// Mobile is the constrained surface and it gets the opinionated layout:
//
//     HOME   VAULT   (◉ CREATE)   EXPLORE   ROUTINE          ⌂ account → header
//
// CREATE is the raised centre action, not a tab. It is the single action the
// product depends on and 44% of registered accounts have never performed it.
// HOME takes the first slot it used to occupy, because CREATE was also serving
// as the home screen — a returning member was landed on a capture viewport.
//
// ACCOUNT leaves the tab bar entirely and becomes a control in the top-right of
// the header. It is a settings surface, not a destination anyone navigates to
// in a session, and it was spending a fifth of the bar to say so.
//
// Desktop keeps a flat rail — there is no space pressure there, so CREATE stays
// an ordinary row and ACCOUNT stays pinned to the bottom of the rail (where Log
// Out used to be; sign out lives on the account page itself).
const HOME_ITEM = { to: ROUTES.HOME, label: 'HOME', icon: <LuHouse size={16} /> };
const VAULT_ITEM = { to: ROUTES.VAULT, label: 'VAULT', icon: <LuLandmark size={16} />, matchPrefix: '/vault' };
const ROUTINE_ITEM = { to: ROUTES.ROUTINE, label: 'ROUTINE', icon: <LuClipboardList size={16} />, matchPrefix: '/routine' };
const EXPLORE_ITEM = { to: ROUTES.JUDGE, label: 'EXPLORE', icon: <LuCompass size={16} />, matchPrefix: ROUTES.JUDGE };

const DEFAULT_NAV_ITEMS = [
  HOME_ITEM,
  { to: ROUTES.CREATE, label: 'CREATE', icon: <LuFocus size={16} /> },
  VAULT_ITEM,
  ROUTINE_ITEM,
  EXPLORE_ITEM,
];

// Split 2 · FAB · 2 by BottomTabBar (it halves the list around the centre item).
const MOBILE_NAV_ITEMS = [HOME_ITEM, VAULT_ITEM, EXPLORE_ITEM, ROUTINE_ITEM];
const MOBILE_CENTER_ITEM = { to: ROUTES.CREATE, label: 'CREATE', icon: <LuFocus size={22} /> };

const ACCOUNT_ITEM = { to: ROUTES.ACCOUNT, label: 'ACCOUNT', icon: <LuUser size={16} /> };

const SHRINK_AT = 6; // px scrolled before the header condenses

// Sticky, scroll-condensing page header. On scroll it tightens its padding and
// gains a blurred backdrop; the per-page subtitles (rendered via HeaderSubtitle)
// collapse away, which is what shrinks the header. The scrolled flag is shared
// down through context so every subtitle reacts in sync. Generic over any
// header content — the title and controls stay put, the subtitle disappears.
const StickyHeader = ({ children }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SHRINK_AT);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <HeaderScrollContext.Provider value={scrolled}>
      <div
        className={[
          'sticky top-0 z-30 px-5 lg:px-12',
          scrolled ? 'pt-3 lg:pt-4 pb-3 lg:pb-4' : 'pt-8 lg:pt-10 pb-4 lg:pb-5',
          'transition-[padding-top,padding-bottom,background-color,border-color] duration-300 ease-mogged',
          scrolled
            ? 'bg-[#0b0a09]/90 backdrop-blur-md border-b border-hairline-soft'
            : 'bg-transparent border-b border-transparent',
        ].join(' ')}
      >
        {children}
      </div>
    </HeaderScrollContext.Provider>
  );
};

// When fitViewport is set, the shell is pinned to the viewport (100dvh) and the
// content region fills exactly the space left under the header — used by Create,
// an app-like capture screen whose body flexes to fit so there is no leftover
// space to scroll. The region still scrolls as a fallback if a mode's content
// genuinely overflows. Otherwise the document body scrolls normally.
const AppLayout = ({
  children,
  header = null,
  fitViewport = false,
  navItems = DEFAULT_NAV_ITEMS,
  mobileNavItems = MOBILE_NAV_ITEMS,
  mobileCenterItem = MOBILE_CENTER_ITEM,
  accountItem = ACCOUNT_ITEM,
  className = '',
}) => (
  <HeaderAccountContext.Provider value={accountItem}>
  <div className={[fitViewport ? 'h-[100dvh] overflow-hidden' : 'min-h-screen', 'flex'].join(' ')}>
    <AppShellSidebar items={navItems} accountItem={accountItem} />
    <div className="flex-1 min-w-0 flex flex-col">
      {/* Above the sticky header, and NOT sticky itself — a persistent offer
          that also refuses to scroll away is the pattern everyone has learned
          to resent. It scrolls off and the header takes over.

          Suppressed on fitViewport surfaces: those pin to 100dvh with overflow
          hidden, so every pixel the bar takes comes straight out of the capture
          viewport. Create is the only one, and it already carries
          UnlockGateModal as its upsell. */}
      <ProBanner suppressed={fitViewport} />
      {header != null && <StickyHeader>{header}</StickyHeader>}
      <main
        className={[
          fitViewport ? 'flex-1 min-h-0 overflow-y-auto flex flex-col' : '',
          // Mobile clears the bottom tab bar; when the AdSense banner is enabled
          // it sits above the bar, so reserve extra room for it (desktop: none).
          //
          // The bar is 72px but the CREATE disc is raised 18px above its top
          // edge (56px tall, +5px ring), so the real obstruction is ~106px. At
          // the old 88px the last element on the page — on Home, a full-width
          // CTA — sat underneath the disc at the bottom of the scroll.
          adsEnabled()
            ? 'pb-[calc(164px+env(safe-area-inset-bottom))] lg:pb-0'
            : 'pb-[calc(106px+env(safe-area-inset-bottom))] lg:pb-0',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </main>
    </div>
    <BannerAd />
    <BottomTabBar items={mobileNavItems} centerItem={mobileCenterItem} />
    {/* First-run tutorial — self-gating; renders only for a fresh, onboarded
        user who hasn't completed it. Mounted here so it layers over every
        in-app surface (the user lands on /create after onboarding). */}
    <TutorialOverlay />
  </div>
  </HeaderAccountContext.Provider>
);

export default AppLayout;
